import React, { FC, ReactElement, useEffect } from 'react'
import { Icons } from '../../Shared/Ant'
import { Button, Checkbox, Pagination, Row, Col } from 'antd'
import { OrderNotification } from './OrderNotification'
import { NotificationsSkeleton } from './Skeleton/NotificationsSkeleton'
import { SortNotification } from './SortNotification'
import { Snapdocs } from '../../../hooks/api'
import { Requests, ReactQuery } from '../../../api'
import { get } from 'lodash'
import MarkAsReadBulk from './MarkAsReadBulk'
import { pusherProvider } from '../../../utils/pusherProvider'
import { ChannelEvents } from '../../../models/event/channelEvents'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { NotificationsSearchParams } from '../types'

export const OrderNotifications: FC<{
  isRootCompany: boolean
  searchParams: NotificationsSearchParams
  changeSearchParams: (params: NotificationsSearchParams) => void
}> = ({ isRootCompany, searchParams, changeSearchParams }): ReactElement => {
  const localstorage = window.localStorage
  const owner = searchParams.owner || 'all'
  const page = searchParams.page || 1
  const status = searchParams.status || localstorage.getItem('status') || 'unread'
  const sort = searchParams.sort || localstorage.getItem('sort') || 'received_at_desc'
  const { path: newOrderPath } = Requests.order({ id: 'new' })
  const queryClient = useQueryClient()

  const notificationsRequest = Requests.notifications({ owner, status, sort, page })
  const { data: notificationsData, isLoading: areNotificationsLoading } = useQuery({
    queryKey: [notificationsRequest.type, { owner, status, sort, page }],
    queryFn: () => ReactQuery.fetchResources(notificationsRequest)
  })

  const unreadCountRequest = Requests.unreadNotificationsCount({ owner })
  const { data: unreadCountData } = useQuery({
    queryKey: [unreadCountRequest.type, { owner }],
    queryFn: () => ReactQuery.fetchResources(unreadCountRequest)
  })

  const { response: appConfig } = Snapdocs.useResources(Requests.appConfig())

  const renderNotifications = get(notificationsData, 'resources', []).map((notification: any) => (
    <OrderNotification key={notification.id} notification={notification} status={status} />
  ))

  const changeStatus = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStatus = e.target.checked ? 'unread' : 'all'
    localstorage.setItem('status', newStatus)
    changeSearchParams({ ...searchParams, status: newStatus, page: 1 })
  }

  const changeSort = (value: string) => {
    localstorage.setItem('sort', value)
    changeSearchParams({ ...searchParams, sort: value, page: 1 })
  }

  const changePage = (page: number) => {
    changeSearchParams({ ...searchParams, page })
  }

  const documentTitle = () => document.title.split(') ').pop() || ''

  useEffect(() => {
    const unreadNotificationsCount = get(unreadCountData, 'resources.unreadCount', 0)
    document.title = unreadNotificationsCount > 0 ? `(${unreadNotificationsCount}) ${documentTitle()}` : documentTitle()
  }, [unreadCountData])

  useEffect(() => {
    if (!appConfig) return

    const pusher = pusherProvider(appConfig.pusherAppKey, appConfig.pusherAppCluster)

    const channels = appConfig.companyChannels.map((channelName: string) => pusher.subscribe(channelName))

    for (const channel of channels) {
      channel.bind(ChannelEvents.OrderNotificationAdded, () => {
        queryClient.invalidateQueries({ queryKey: [notificationsRequest.type] })
        queryClient.invalidateQueries({ queryKey: [unreadCountRequest.type] })
      })
    }

    return () => {
      for (const channel of channels) {
        channel.unbind(ChannelEvents.OrderNotificationAdded)
      }
    }
  }, [appConfig, queryClient, notificationsRequest.type, unreadCountRequest.type])

  if (areNotificationsLoading) {
    return <NotificationsSkeleton />
  }

  return (
    <div className="content" style={{ marginRight: 13 }}>
      <div className="order-notifications" data-testid="order-notifications">
        <Row className="header" align="middle">
          <Col span="18">
            <SortNotification selectedValue={sort} handleChange={changeSort} />
            <Checkbox onChange={changeStatus} checked={status === 'unread'} data-test="only-show-unread-notifications">
              Only show unread notifications ({get(unreadCountData, 'resources.unreadCount', 0)})
            </Checkbox>
            <MarkAsReadBulk unreadCount={get(unreadCountData, 'resources.unreadCount', 0)} />
          </Col>
          {!isRootCompany && (
            <Col span="6">
              <div className="text-right">
                <Button type="primary" href={newOrderPath}>
                  + Create order
                </Button>
              </div>
            </Col>
          )}
        </Row>
        {get(notificationsData, 'resources', []).length > 0 ? (
          renderNotifications
        ) : (
          <Row>
            <Col span="24" className="no-new-notifications text-center">
              <Icons.Bell className="icon" />
              <div className="text">No notifications at this time</div>
            </Col>
          </Row>
        )}
        <Row className="footer">
          <Col span="12" offset="6" className="text-center">
            <Pagination
              showQuickJumper
              showSizeChanger={false}
              hideOnSinglePage={true}
              current={get(notificationsData, 'meta.currentPage')}
              pageSize={get(notificationsData, 'meta.perPage')}
              total={get(notificationsData, 'meta.totalEntries')}
              onChange={changePage}
            />
          </Col>
        </Row>
      </div>
    </div>
  )
}
